import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type ProposalPayment,
  type ProposalPaymentPayload,
  proposalApi,
} from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import type { PaymentFormState } from '@/hooks/commercial/use-cart-step-validation';
import { toast } from '@/hooks/use-toast';
import { centsToPtbr } from '@/lib/formatters';
import type { ICartItemBreakdown, ICartPaymentSummary } from '@/types/commercial/cart';
import type { CartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';

type PaymentType = 'CASH' | 'INSTALLMENTS' | '';

const PAYMENT_OPTIONS = [
  { value: 'CASH', label: 'À vista' },
  { value: 'INSTALLMENTS', label: 'A prazo' },
] as const;

const INSTALLMENT_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function formatCurrencyInput(digits: string): string {
  if (!digits) return '';
  return centsToPtbr(Number(digits));
}

function fmtCents(cents: number): string {
  return `R$ ${centsToPtbr(cents)}`;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">{children}</p>;
}

function SummaryRow({
  label,
  value,
  subtitle,
  bold,
  green,
}: {
  label: string;
  value: string;
  subtitle?: React.ReactNode;
  bold?: boolean;
  green?: boolean;
}) {
  return (
    <div className="flex items-start justify-between text-xs">
      <span
        className={
          bold ? 'font-semibold text-gray-900' : green ? 'text-green-600' : 'text-gray-600'
        }
      >
        {label}
      </span>
      <div className="text-right">
        <span
          className={`${bold ? 'font-semibold text-gray-900' : 'text-gray-900'} ${green ? 'text-green-600' : ''}`}
        >
          {value}
        </span>
        {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

interface StepCartPaymentPanelProps {
  proposalId?: string;
  savedPayment?: ProposalPayment | null;
  breakdown: ICartItemBreakdown | null;
  totalDiscountInCents: number;
  cartSummaryCategories: CartSummaryCategories;
  paymentSummary: ICartPaymentSummary | null;
  isRecurringOnly: boolean;
  hasCartItems: boolean;
  allowedPaymentMethods?: string[];
  canUseInstallments?: boolean;
  installmentsRestrictionReason?: string | null;
  minEntrancePercent?: number;
  onPaymentStateChange?: (state: PaymentFormState) => void;
  onSavePaymentRef?: (saveFn: () => Promise<void>) => void;
}

export function StepCartPaymentPanel({
  proposalId,
  savedPayment,
  breakdown,
  totalDiscountInCents,
  cartSummaryCategories,
  paymentSummary,
  isRecurringOnly,
  hasCartItems,
  allowedPaymentMethods,
  canUseInstallments = true,
  installmentsRestrictionReason,
  minEntrancePercent = 0,
  onPaymentStateChange,
  onSavePaymentRef,
}: StepCartPaymentPanelProps) {
  const queryClient = useQueryClient();

  const [paymentType, setPaymentType] = useState<PaymentType>('');
  const [dueDay, setDueDay] = useState('');
  const [hasEntrance, setHasEntrance] = useState(false);
  const [entrancePercent, setEntrancePercent] = useState('');
  const [freightOnEntrance, setFreightOnEntrance] = useState(true);
  const [installmentCount, setInstallmentCount] = useState('');
  const [freightDigits, setFreightDigits] = useState('');
  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);
  const [entranceError, setEntranceError] = useState<string | null>(null);

  const hasHydratedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional one-time hydration
  useEffect(() => {
    if (!savedPayment || !hasCartItems || hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    setPaymentType(savedPayment.paymentType ?? '');
    setDueDay(savedPayment.dueDay ? String(savedPayment.dueDay) : '');
    setHasEntrance(savedPayment.hasDownPayment ?? false);
    setEntrancePercent(
      savedPayment.downPaymentPercentage != null ? String(savedPayment.downPaymentPercentage) : '',
    );
    setFreightOnEntrance(savedPayment.freteInEntrada ?? true);
    setInstallmentCount(savedPayment.installments ? String(savedPayment.installments) : '');
    setFreightDigits(savedPayment.freteInCents ? String(savedPayment.freteInCents) : '');
  }, [savedPayment, hasCartItems]);

  const savePaymentMutation = useMutation({
    mutationFn: (payment: ProposalPaymentPayload) => {
      if (!proposalId) throw new Error('proposalId is required');
      return proposalApi.setPayment(proposalId, payment);
    },
    onSuccess: () => {
      setEntranceError(null);
      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
      }
    },
    onError: (error) => {
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
      const serverField = axiosError.response?.data?.error;
      if (serverField === 'entradaInCents') {
        setEntranceError(`Entrada mínima permitida: ${minEntrancePercent}%`);
        return;
      }
      toast({ title: 'Erro ao salvar pagamento', variant: 'destructive' });
    },
  });

  const handleSavePayment = useCallback(async () => {
    if (!proposalId || !paymentType) return;

    const freight = Number(freightDigits) || 0;
    const entradaPercentVal = Number(entrancePercent) || 0;

    if (hasEntrance && entradaPercentVal <= 0) return;

    const payload: ProposalPaymentPayload = {
      paymentType: paymentType as 'CASH' | 'INSTALLMENTS',
      dueDay: Number(dueDay) || 1,
      installments: Number(installmentCount) || 1,
      freteInCents: freight,
      freteInEntrada: freightOnEntrance,
      hasDownPayment: hasEntrance,
      downPaymentPercentage: hasEntrance ? entradaPercentVal : undefined,
    };

    await savePaymentMutation.mutateAsync(payload);
  }, [
    dueDay,
    entrancePercent,
    freightDigits,
    freightOnEntrance,
    hasEntrance,
    installmentCount,
    paymentType,
    proposalId,
    savePaymentMutation,
  ]);

  useEffect(() => {
    onSavePaymentRef?.(handleSavePayment);
  }, [handleSavePayment, onSavePaymentRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional auto-save trigger
  useEffect(() => {
    if (!proposalId || !paymentType || !hasCartItems) return;

    if (hasEntrance && minEntrancePercent > 0) {
      const pct = Number(entrancePercent) || 0;
      if (pct > 0 && pct < minEntrancePercent) return;
    }

    const timeout = setTimeout(() => {
      void handleSavePayment().catch(() => {});
    }, 800);
    return () => clearTimeout(timeout);
  }, [
    paymentType,
    dueDay,
    hasEntrance,
    entrancePercent,
    freightOnEntrance,
    installmentCount,
    freightDigits,
    breakdown,
    minEntrancePercent,
  ]);

  const effectivePaymentMethods = allowedPaymentMethods ?? ['CASH', 'INSTALLMENTS'];
  const isInstallmentsAllowed =
    canUseInstallments && effectivePaymentMethods.includes('INSTALLMENTS');
  const effectiveRestrictionReason =
    installmentsRestrictionReason ??
    'Pagamento a prazo não disponível para este cliente. Utilize o pagamento à vista.';

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional forced reset / auto-select
  useEffect(() => {
    if (!hasCartItems) return;
    const mustUseCash = !isInstallmentsAllowed || isRecurringOnly;
    // Auto-select CASH when installments are restricted and nothing is selected,
    // or reset from INSTALLMENTS if credit analysis no longer allows it.
    if (mustUseCash && paymentType !== 'CASH') {
      setPaymentType('CASH');
    }
  }, [hasCartItems, isInstallmentsAllowed, isRecurringOnly, paymentType]);

  useEffect(() => {
    if (!hasCartItems || canUseInstallments) return;
    setRestrictionModalOpen(true);
  }, [canUseInstallments, hasCartItems]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — fire when any field changes
  useEffect(() => {
    onPaymentStateChange?.({
      paymentType,
      dueDay,
      installmentCount,
      hasEntrance,
      entrancePercent,
    });
  }, [paymentType, dueDay, installmentCount, hasEntrance, entrancePercent]);

  const basePaymentOptions = PAYMENT_OPTIONS.filter((o) =>
    effectivePaymentMethods.includes(o.value),
  );
  const filteredPaymentOptions = isRecurringOnly
    ? basePaymentOptions.filter((o) => o.value === 'CASH')
    : basePaymentOptions;
  const summary = paymentSummary;

  const freightDisplayValue = summary?.shippingInCents ?? (Number(freightDigits) || 0);
  const hasFreight = freightDisplayValue > 0;
  const isInstallmentPayment = paymentType === 'INSTALLMENTS';

  const hasProducts = (breakdown?.products.totalItems ?? 0) > 0;
  const hasAccessories = (breakdown?.accessories.totalItems ?? 0) > 0;
  const hasConnectivity = (breakdown?.connectivity.totalItems ?? 0) > 0;
  const hasMonthlyServices = (breakdown?.monthlyServices.totalItems ?? 0) > 0;
  const hasYearlyServices = (breakdown?.yearlyServices.totalItems ?? 0) > 0;
  const hasFixedServices = (breakdown?.fixedServices.totalItems ?? 0) > 0;
  const hasAnyItemSection =
    hasProducts ||
    hasAccessories ||
    hasConnectivity ||
    hasMonthlyServices ||
    hasYearlyServices ||
    hasFixedServices;

  const installmentLabel = summary?.installmentTotal
    ? `${hasEntrance ? 'Entrada + ' : ''}${summary.installmentTotal.installments}x`
    : undefined;
  const renderDiscountTag = (discountInCents: number) =>
    discountInCents > 0 ? (
      <>
        {' | '}
        <span className="text-green-600">Desconto {fmtCents(discountInCents)}</span>
      </>
    ) : null;
  const showTotalParcelado = paymentType === 'INSTALLMENTS' && !!summary?.installmentTotal;
  const showTotalAVista = paymentType === 'CASH' && !!summary?.cashTotal;
  const isFreightInInstallments = isInstallmentPayment && hasFreight && !freightOnEntrance;
  const parceladoComFreteInCents = summary?.installmentTotal
    ? Math.max(
        0,
        summary.installmentTotal.totalInCents - summary.installmentTotal.downPaymentInCents,
      )
    : 0;
  const showRecurringConnectivity =
    (summary?.recurringConnectivity?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringMonthlyServices =
    (summary?.recurringMonthlyServices?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringYearlyServices =
    (summary?.recurringYearlyServices?.yearlyPaymentInCents ?? 0) > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Pagamento</CardTitle>
      </CardHeader>

      <CardContent className="space-y-3.5">
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-gray-700">Tipo de pagamento</span>
          <Select
            value={paymentType}
            onValueChange={(v) => {
              if (v === 'INSTALLMENTS' && !isInstallmentsAllowed) {
                setRestrictionModalOpen(true);
                return;
              }
              setPaymentType(v as PaymentType);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {filteredPaymentOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={restrictionModalOpen} onOpenChange={setRestrictionModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Atenção</DialogTitle>
              <DialogDescription>{effectiveRestrictionReason}</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-gray-700">
              Para continuar com esta proposta, utilize pagamento à vista no carrinho.
            </p>
            <DialogFooter>
              <Button onClick={() => setRestrictionModalOpen(false)}>Entendi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {hasCartItems && (
          <>
            {paymentType === 'INSTALLMENTS' && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-gray-700">Vencimento</span>
                <Select value={dueDay} onValueChange={setDueDay}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Selecione o dia" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        Dia {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {paymentType === 'INSTALLMENTS' && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">Entrada</span>
                  <Switch checked={hasEntrance} onCheckedChange={setHasEntrance} />
                </div>

                {hasEntrance && (
                  <>
                    <div className="space-y-1.5">
                      <span className="text-xs font-medium text-gray-700">
                        Porcentagem de entrada
                      </span>
                      <div className="relative">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          className="h-8 pr-8 text-xs"
                          value={entrancePercent}
                          onChange={(e) => {
                            setEntrancePercent(e.target.value);
                            setEntranceError(null);
                          }}
                          placeholder="0"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          %
                        </span>
                      </div>
                      {hasEntrance &&
                        minEntrancePercent > 0 &&
                        (Number(entrancePercent) || 0) > 0 &&
                        (Number(entrancePercent) || 0) < minEntrancePercent && (
                          <p className="text-xs text-destructive">
                            Entrada mínima permitida: {minEntrancePercent}%
                          </p>
                        )}
                      {entranceError &&
                        !(
                          hasEntrance &&
                          minEntrancePercent > 0 &&
                          (Number(entrancePercent) || 0) > 0 &&
                          (Number(entrancePercent) || 0) < minEntrancePercent
                        ) && <p className="text-xs text-destructive">{entranceError}</p>}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Frete na entrada ou 1ª parcela
                      </span>
                      <Switch checked={freightOnEntrance} onCheckedChange={setFreightOnEntrance} />
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-700">Parcelas</span>
                  <Select value={installmentCount} onValueChange={setInstallmentCount}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {INSTALLMENT_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}x
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Frete</span>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                  R$
                </span>
                <Input
                  className="h-8 pl-9 text-xs"
                  value={freightDigits ? formatCurrencyInput(freightDigits) : ''}
                  onChange={(e) => setFreightDigits(e.target.value.replace(/\D/g, ''))}
                  placeholder="0,00"
                />
              </div>
            </div>

            {hasAnyItemSection && (
              <>
                <Separator className="my-1" />

                <div className="space-y-1.5">
                  <SectionTitle>Itens</SectionTitle>

                  {hasProducts && breakdown && (
                    <SummaryRow
                      label="Produtos"
                      value={fmtCents(cartSummaryCategories.products.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.products.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasAccessories && breakdown && (
                    <SummaryRow
                      label="Acessórios"
                      value={fmtCents(cartSummaryCategories.accessories.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.accessories.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasConnectivity && breakdown && (
                    <SummaryRow
                      label="Conectividade"
                      value={fmtCents(cartSummaryCategories.connectivity.netInCents)}
                      subtitle={
                        <>
                          {'Mensal recorrente'}
                          {renderDiscountTag(cartSummaryCategories.connectivity.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasMonthlyServices && breakdown && (
                    <SummaryRow
                      label="Serviços mensais"
                      value={fmtCents(cartSummaryCategories.monthlyServices.grossInCents)}
                      subtitle={
                        <>
                          {'Mensal recorrente'}
                          {renderDiscountTag(cartSummaryCategories.monthlyServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasYearlyServices && breakdown && (
                    <SummaryRow
                      label="Serviços anuais"
                      value={fmtCents(cartSummaryCategories.yearlyServices.grossInCents)}
                      subtitle={
                        <>
                          {'Anual recorrente'}
                          {renderDiscountTag(cartSummaryCategories.yearlyServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasFixedServices && breakdown && (
                    <SummaryRow
                      label="Serviços fixos"
                      value={fmtCents(cartSummaryCategories.fixedServices.grossInCents)}
                      subtitle={
                        <>
                          {paymentType === 'CASH' ? 'À vista' : installmentLabel}
                          {renderDiscountTag(cartSummaryCategories.fixedServices.discountInCents)}
                        </>
                      }
                    />
                  )}

                  {hasFreight && (
                    <SummaryRow
                      label="Frete"
                      value={fmtCents(freightDisplayValue)}
                      subtitle={
                        paymentType === 'CASH'
                          ? 'À vista'
                          : freightOnEntrance
                            ? 'Entrada'
                            : 'Parcelamento'
                      }
                    />
                  )}

                  {totalDiscountInCents > 0 && (
                    <SummaryRow label="Desconto" value={fmtCents(totalDiscountInCents)} green />
                  )}
                </div>
              </>
            )}

            {showTotalAVista && summary.cashTotal && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total à vista</SectionTitle>
                  <SummaryRow
                    label="Boleto (1x) ou Pix"
                    value={fmtCents(summary.cashTotal.totalInCents)}
                  />
                  {totalDiscountInCents > 0 && (
                    <SummaryRow
                      label="Total de descontos"
                      value={fmtCents(totalDiscountInCents)}
                      green
                    />
                  )}
                  <SummaryRow label="Total" value={fmtCents(summary.cashTotal.totalInCents)} bold />
                </div>
              </>
            )}

            {showTotalParcelado && summary.installmentTotal && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Parcelado</SectionTitle>
                  {summary.installmentTotal.downPaymentInCents > 0 && (
                    <SummaryRow
                      label={
                        hasFreight && freightOnEntrance
                          ? 'Entrada + Frete'
                          : summary.installmentTotal.downPaymentLabel
                      }
                      value={fmtCents(summary.installmentTotal.downPaymentInCents)}
                    />
                  )}
                  {isFreightInInstallments && (
                    <SummaryRow
                      label="Parcelado com frete"
                      value={fmtCents(parceladoComFreteInCents)}
                    />
                  )}
                  {summary.installmentTotal.installmentBreakdown && (
                    <SummaryRow
                      label={`Boleto (${summary.installmentTotal.installments}x)`}
                      value={fmtCents(
                        summary.installmentTotal.installmentBreakdown.installmentInCents,
                      )}
                    />
                  )}
                  {totalDiscountInCents > 0 && (
                    <SummaryRow
                      label="Total de descontos"
                      value={fmtCents(totalDiscountInCents)}
                      green
                    />
                  )}
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.installmentTotal.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringConnectivity && summary?.recurringConnectivity && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (SIM Card)</SectionTitle>
                  <SummaryRow
                    label="Boleto (mensal)"
                    value={fmtCents(summary.recurringConnectivity.monthlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringConnectivity.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringMonthlyServices && summary?.recurringMonthlyServices && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (Serviços mensais)</SectionTitle>
                  <SummaryRow
                    label="Boleto (mensal)"
                    value={fmtCents(summary.recurringMonthlyServices.monthlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringMonthlyServices.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}

            {showRecurringYearlyServices && summary?.recurringYearlyServices && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1.5">
                  <SectionTitle>Total Recorrente (Serviços anuais)</SectionTitle>
                  <SummaryRow
                    label="Boleto (anual)"
                    value={fmtCents(summary.recurringYearlyServices.yearlyPaymentInCents)}
                  />
                  <SummaryRow
                    label="Total"
                    value={fmtCents(summary.recurringYearlyServices.totalInCents)}
                    bold
                  />
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
